import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import {
  DollarSign,
  TrendingUp,
  FileText,
  CreditCard,
  Users,
  Calendar,
  Download,
  ChevronDown,
  ChevronUp,
  Loader2,
  AlertCircle,
  Heart,
  X,
} from 'lucide-react';
import { cn } from '../lib/cn';

interface InvoiceDetail {
  id: string;
  invoiceNumber: string;
  type: string;
  status: string;
  description: string;
  subtotal: number;
  tax: number;
  total: number;
  amountPaid: number;
  balance: number;
  issueDate: string;
  dueDate: string;
  paidDate: string | null;
  member: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    addressStreet1?: string;
    addressCity?: string;
    addressState?: string;
    addressPostalCode?: string;
  };
  lineItems: {
    id: string;
    description: string;
    quantity: number;
    unitPrice: number;
    total: number;
  }[];
  payments: {
    id: string;
    amount: number;
    method: string;
    status: string;
    processedAt: string;
  }[];
}

type TabId = 'overview' | 'donations' | 'invoices' | 'payments';

interface FinancialOverview {
  revenue: {
    thisMonth: number;
    lastMonth: number;
    thisYear: number;
    percentChange: number;
  };
  donations: {
    count: number;
    total: number;
    averageAmount: number;
  };
  invoices: {
    outstanding: number;
    overdue: number;
    paid: number;
  };
  recentPayments: {
    id: string;
    amount: number;
    method: string;
    processedAt: string;
    member: { firstName: string; lastName: string } | null;
  }[];
  monthlyTrends: {
    month: string;
    revenue: number;
    donations: number;
    payments: number;
  }[];
}

interface DonationReport {
  summary: {
    totalDonations: number;
    totalAmount: number;
    averageAmount: number;
    uniqueDonors: number;
  };
  byCampaign: {
    campaignId: string | null;
    campaignName: string;
    count: number;
    total: number;
  }[];
  topDonors: {
    memberId: string;
    name: string;
    totalDonations: number;
    donationCount: number;
  }[];
  donations: {
    id: string;
    amount: number;
    campaign: { name: string } | null;
    member: { firstName: string; lastName: string } | null;
    donorName: string | null;
    createdAt: string;
    status: string;
  }[];
}

interface InvoiceReport {
  summary: {
    totalOutstanding: number;
    totalOverdue: number;
    totalPaid: number;
    invoiceCount: number;
  };
  aging: {
    current: number;
    thirtyDays: number;
    sixtyDays: number;
    ninetyDays: number;
    overNinety: number;
  };
  invoices: {
    id: string;
    invoiceNumber: string;
    member: { firstName: string; lastName: string };
    total: number;
    balance: number;
    status: string;
    dueDate: string;
    issueDate: string;
  }[];
}

interface PaymentReport {
  summary: {
    totalPayments: number;
    totalAmount: number;
    averageAmount: number;
  };
  byMethod: {
    method: string;
    count: number;
    total: number;
  }[];
  payments: {
    id: string;
    amount: number;
    method: string;
    processedAt: string;
    invoice: { invoiceNumber: string } | null;
    member: { firstName: string; lastName: string } | null;
  }[];
}

const tabs: { id: TabId; name: string; icon: typeof DollarSign }[] = [
  { id: 'overview', name: 'Overview', icon: TrendingUp },
  { id: 'donations', name: 'Donations', icon: Heart },
  { id: 'invoices', name: 'Invoices', icon: FileText },
  { id: 'payments', name: 'Payments', icon: CreditCard },
];

export function FinancesPage() {
  const [activeTab, setActiveTab] = useState<TabId>('overview');
  const [dateRange, setDateRange] = useState('thisMonth');

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Finances</h1>
          <p className="mt-1 text-sm text-gray-500">
            Financial reports and analytics
          </p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
          >
            <option value="thisMonth">This Month</option>
            <option value="lastMonth">Last Month</option>
            <option value="thisQuarter">This Quarter</option>
            <option value="thisYear">This Year</option>
            <option value="lastYear">Last Year</option>
          </select>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={cn(
                'flex items-center gap-2 border-b-2 py-3 px-1 text-sm font-medium transition-colors',
                activeTab === tab.id
                  ? 'border-primary-600 text-primary-600'
                  : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
              )}
            >
              <tab.icon className="h-4 w-4" />
              {tab.name}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && <OverviewTab />}
      {activeTab === 'donations' && <DonationsTab />}
      {activeTab === 'invoices' && <InvoicesTab />}
      {activeTab === 'payments' && <PaymentsTab onViewInvoice={() => setActiveTab('invoices')} />}
    </div>
  );
}

function OverviewTab() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['financial-overview'],
    queryFn: async () => {
      const response = await api.get<FinancialOverview>('/reports/financial/overview');
      if (!response.success) throw new Error(response.error?.message);
      return response.data!;
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="rounded-lg bg-red-50 p-4 text-red-700">
        <div className="flex items-center gap-2">
          <AlertCircle className="h-5 w-5" />
          <span>Failed to load financial overview</span>
        </div>
      </div>
    );
  }

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);

  return (
    <div className="space-y-6">
      {/* Revenue Cards */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <div className="rounded-xl bg-white p-6 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-500">This Month</span>
            <div className={cn(
              'flex items-center gap-1 text-sm font-medium',
              data.revenue.percentChange >= 0 ? 'text-green-600' : 'text-red-600'
            )}>
              {data.revenue.percentChange >= 0 ? (
                <ChevronUp className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
              {Math.abs(data.revenue.percentChange).toFixed(1)}%
            </div>
          </div>
          <p className="mt-2 text-3xl font-bold text-gray-900">
            {formatCurrency(data.revenue.thisMonth)}
          </p>
          <p className="mt-1 text-sm text-gray-500">
            vs {formatCurrency(data.revenue.lastMonth)} last month
          </p>
        </div>

        <div className="rounded-xl bg-white p-6 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-500">Year to Date</span>
            <DollarSign className="h-5 w-5 text-gray-400" />
          </div>
          <p className="mt-2 text-3xl font-bold text-gray-900">
            {formatCurrency(data.revenue.thisYear)}
          </p>
        </div>

        <div className="rounded-xl bg-white p-6 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-500">Donations</span>
            <Heart className="h-5 w-5 text-gray-400" />
          </div>
          <p className="mt-2 text-3xl font-bold text-gray-900">
            {formatCurrency(data.donations.total)}
          </p>
          <p className="mt-1 text-sm text-gray-500">
            {data.donations.count} donations, avg {formatCurrency(data.donations.averageAmount)}
          </p>
        </div>

        <div className="rounded-xl bg-white p-6 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-500">Outstanding</span>
            <FileText className="h-5 w-5 text-gray-400" />
          </div>
          <p className="mt-2 text-3xl font-bold text-gray-900">
            {formatCurrency(data.invoices.outstanding)}
          </p>
          <p className="mt-1 text-sm text-red-600">
            {formatCurrency(data.invoices.overdue)} overdue
          </p>
        </div>
      </div>

      {/* Monthly Trends Chart (simple bar representation) */}
      <div className="rounded-xl bg-white p-6 shadow-sm border border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Monthly Trends</h3>
        <div className="space-y-3">
          {data.monthlyTrends.map((month) => {
            const maxValue = Math.max(...data.monthlyTrends.map((m) => m.revenue));
            const barWidth = maxValue > 0 ? (month.revenue / maxValue) * 100 : 0;
            return (
              <div key={month.month} className="flex items-center gap-4">
                <span className="w-12 text-sm text-gray-500">{month.month}</span>
                <div className="flex-1 h-6 bg-gray-100 rounded overflow-hidden">
                  <div
                    className="h-full bg-primary-500 rounded"
                    style={{ width: `${barWidth}%` }}
                  />
                </div>
                <span className="w-24 text-right text-sm font-medium text-gray-900">
                  {formatCurrency(month.revenue)}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Recent Payments */}
      <div className="rounded-xl bg-white p-6 shadow-sm border border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Payments</h3>
        <div className="divide-y divide-gray-200">
          {data.recentPayments.length === 0 ? (
            <p className="py-4 text-sm text-gray-500 text-center">No recent payments</p>
          ) : (
            data.recentPayments.map((payment) => (
              <div key={payment.id} className="flex items-center justify-between py-3">
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    {payment.member
                      ? `${payment.member.firstName} ${payment.member.lastName}`
                      : 'Unknown'}
                  </p>
                  <p className="text-xs text-gray-500">
                    {payment.method} &middot;{' '}
                    {new Date(payment.processedAt).toLocaleDateString()}
                  </p>
                </div>
                <span className="font-medium text-green-600">
                  +{formatCurrency(payment.amount)}
                </span>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

function DonationsTab() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['donation-report'],
    queryFn: async () => {
      const response = await api.get<DonationReport>('/reports/financial/donations');
      if (!response.success) throw new Error(response.error?.message);
      return response.data!;
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="rounded-lg bg-red-50 p-4 text-red-700">
        <div className="flex items-center gap-2">
          <AlertCircle className="h-5 w-5" />
          <span>Failed to load donation report</span>
        </div>
      </div>
    );
  }

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <div className="rounded-xl bg-white p-6 shadow-sm border border-gray-200">
          <span className="text-sm font-medium text-gray-500">Total Donations</span>
          <p className="mt-2 text-3xl font-bold text-gray-900">
            {formatCurrency(data.summary.totalAmount)}
          </p>
        </div>
        <div className="rounded-xl bg-white p-6 shadow-sm border border-gray-200">
          <span className="text-sm font-medium text-gray-500">Number of Donations</span>
          <p className="mt-2 text-3xl font-bold text-gray-900">{data.summary.totalDonations}</p>
        </div>
        <div className="rounded-xl bg-white p-6 shadow-sm border border-gray-200">
          <span className="text-sm font-medium text-gray-500">Average Donation</span>
          <p className="mt-2 text-3xl font-bold text-gray-900">
            {formatCurrency(data.summary.averageAmount)}
          </p>
        </div>
        <div className="rounded-xl bg-white p-6 shadow-sm border border-gray-200">
          <span className="text-sm font-medium text-gray-500">Unique Donors</span>
          <p className="mt-2 text-3xl font-bold text-gray-900">{data.summary.uniqueDonors}</p>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* By Campaign */}
        <div className="rounded-xl bg-white p-6 shadow-sm border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">By Campaign</h3>
          <div className="space-y-3">
            {data.byCampaign.map((campaign) => (
              <div key={campaign.campaignId || 'general'} className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-900">{campaign.campaignName}</p>
                  <p className="text-xs text-gray-500">{campaign.count} donations</p>
                </div>
                <span className="font-medium text-gray-900">{formatCurrency(campaign.total)}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Top Donors */}
        <div className="rounded-xl bg-white p-6 shadow-sm border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Donors</h3>
          <div className="space-y-3">
            {data.topDonors.map((donor, index) => (
              <div key={donor.memberId} className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary-100 text-xs font-medium text-primary-700">
                    {index + 1}
                  </span>
                  <div>
                    <p className="text-sm font-medium text-gray-900">{donor.name}</p>
                    <p className="text-xs text-gray-500">{donor.donationCount} donations</p>
                  </div>
                </div>
                <span className="font-medium text-gray-900">
                  {formatCurrency(donor.totalDonations)}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Donations Table */}
      <div className="rounded-xl bg-white shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">Recent Donations</h3>
          <button className="flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700">
            <Download className="h-4 w-4" />
            Export
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Donor
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Campaign
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {data.donations.map((donation) => (
                <tr key={donation.id}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {donation.member
                      ? `${donation.member.firstName} ${donation.member.lastName}`
                      : donation.donorName || 'Anonymous'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {donation.campaign?.name || 'General'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {formatCurrency(donation.amount)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(donation.createdAt).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={cn(
                        'inline-flex rounded-full px-2 py-1 text-xs font-medium',
                        donation.status === 'completed'
                          ? 'bg-green-100 text-green-700'
                          : donation.status === 'pending'
                          ? 'bg-yellow-100 text-yellow-700'
                          : 'bg-gray-100 text-gray-700'
                      )}
                    >
                      {donation.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function InvoicesTab() {
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<string | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['invoice-report'],
    queryFn: async () => {
      const response = await api.get<InvoiceReport>('/reports/financial/invoices');
      if (!response.success) throw new Error(response.error?.message);
      return response.data!;
    },
  });

  const { data: invoiceDetail, isLoading: detailLoading } = useQuery({
    queryKey: ['invoice-detail', selectedInvoiceId],
    queryFn: async () => {
      if (!selectedInvoiceId) return null;
      const response = await api.get<InvoiceDetail>(`/invoices/${selectedInvoiceId}`);
      if (!response.success) throw new Error(response.error?.message);
      return response.data!;
    },
    enabled: !!selectedInvoiceId,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="rounded-lg bg-red-50 p-4 text-red-700">
        <div className="flex items-center gap-2">
          <AlertCircle className="h-5 w-5" />
          <span>Failed to load invoice report</span>
        </div>
      </div>
    );
  }

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'paid':
        return 'bg-green-100 text-green-700';
      case 'partial':
        return 'bg-yellow-100 text-yellow-700';
      case 'overdue':
        return 'bg-red-100 text-red-700';
      case 'sent':
        return 'bg-blue-100 text-blue-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const getMethodLabel = (method: string) => {
    switch (method) {
      case 'credit_card': return 'Credit Card';
      case 'ach': return 'Bank Transfer';
      case 'check': return 'Check';
      case 'cash': return 'Cash';
      default: return method;
    }
  };

  return (
    <div className="space-y-6">
      {/* Invoice Detail Modal */}
      {selectedInvoiceId && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/50" onClick={() => setSelectedInvoiceId(null)} />
            <div className="relative bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900">
                  Invoice {invoiceDetail?.invoiceNumber || '...'}
                </h2>
                <button
                  onClick={() => setSelectedInvoiceId(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {detailLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
                </div>
              ) : invoiceDetail ? (
                <div className="p-6 space-y-6">
                  {/* Header Info */}
                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <h3 className="text-sm font-medium text-gray-500 mb-2">Bill To</h3>
                      <p className="text-gray-900 font-medium">
                        {invoiceDetail.member.firstName} {invoiceDetail.member.lastName}
                      </p>
                      <p className="text-sm text-gray-600">{invoiceDetail.member.email}</p>
                      {invoiceDetail.member.addressStreet1 && (
                        <p className="text-sm text-gray-600 mt-1">
                          {invoiceDetail.member.addressStreet1}<br />
                          {invoiceDetail.member.addressCity}, {invoiceDetail.member.addressState} {invoiceDetail.member.addressPostalCode}
                        </p>
                      )}
                    </div>
                    <div className="text-right">
                      <div className="mb-2">
                        <span className={cn('inline-flex rounded-full px-3 py-1 text-sm font-medium', getStatusColor(invoiceDetail.status))}>
                          {invoiceDetail.status}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600">
                        Issue Date: {new Date(invoiceDetail.issueDate).toLocaleDateString()}
                      </p>
                      <p className="text-sm text-gray-600">
                        Due Date: {new Date(invoiceDetail.dueDate).toLocaleDateString()}
                      </p>
                      {invoiceDetail.paidDate && (
                        <p className="text-sm text-green-600">
                          Paid: {new Date(invoiceDetail.paidDate).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Description */}
                  {invoiceDetail.description && (
                    <div>
                      <h3 className="text-sm font-medium text-gray-500 mb-1">Description</h3>
                      <p className="text-gray-900">{invoiceDetail.description}</p>
                    </div>
                  )}

                  {/* Line Items */}
                  <div>
                    <h3 className="text-sm font-medium text-gray-500 mb-2">Line Items</h3>
                    <div className="border border-gray-200 rounded-lg overflow-hidden">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Description</th>
                            <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Qty</th>
                            <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Price</th>
                            <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Total</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {invoiceDetail.lineItems.map((item) => (
                            <tr key={item.id}>
                              <td className="px-4 py-2 text-sm text-gray-900">{item.description}</td>
                              <td className="px-4 py-2 text-sm text-gray-600 text-right">{item.quantity}</td>
                              <td className="px-4 py-2 text-sm text-gray-600 text-right">{formatCurrency(item.unitPrice)}</td>
                              <td className="px-4 py-2 text-sm font-medium text-gray-900 text-right">{formatCurrency(item.total)}</td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot className="bg-gray-50">
                          <tr>
                            <td colSpan={3} className="px-4 py-2 text-sm font-medium text-gray-900 text-right">Subtotal</td>
                            <td className="px-4 py-2 text-sm font-medium text-gray-900 text-right">{formatCurrency(invoiceDetail.subtotal)}</td>
                          </tr>
                          {invoiceDetail.tax > 0 && (
                            <tr>
                              <td colSpan={3} className="px-4 py-2 text-sm text-gray-600 text-right">Tax</td>
                              <td className="px-4 py-2 text-sm text-gray-600 text-right">{formatCurrency(invoiceDetail.tax)}</td>
                            </tr>
                          )}
                          <tr className="border-t border-gray-300">
                            <td colSpan={3} className="px-4 py-2 text-sm font-bold text-gray-900 text-right">Total</td>
                            <td className="px-4 py-2 text-sm font-bold text-gray-900 text-right">{formatCurrency(invoiceDetail.total)}</td>
                          </tr>
                          <tr>
                            <td colSpan={3} className="px-4 py-2 text-sm text-green-600 text-right">Amount Paid</td>
                            <td className="px-4 py-2 text-sm text-green-600 text-right">{formatCurrency(invoiceDetail.amountPaid)}</td>
                          </tr>
                          <tr className="border-t border-gray-300">
                            <td colSpan={3} className="px-4 py-2 text-sm font-bold text-gray-900 text-right">Balance Due</td>
                            <td className="px-4 py-2 text-sm font-bold text-gray-900 text-right">{formatCurrency(invoiceDetail.balance)}</td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </div>

                  {/* Payment History */}
                  {invoiceDetail.payments.length > 0 && (
                    <div>
                      <h3 className="text-sm font-medium text-gray-500 mb-2">Payment History</h3>
                      <div className="border border-gray-200 rounded-lg overflow-hidden">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Date</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Method</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Status</th>
                              <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Amount</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {invoiceDetail.payments.map((payment) => (
                              <tr key={payment.id}>
                                <td className="px-4 py-2 text-sm text-gray-900">
                                  {new Date(payment.processedAt).toLocaleDateString()}
                                </td>
                                <td className="px-4 py-2 text-sm text-gray-600">{getMethodLabel(payment.method)}</td>
                                <td className="px-4 py-2">
                                  <span className={cn(
                                    'inline-flex rounded-full px-2 py-0.5 text-xs font-medium',
                                    payment.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                                  )}>
                                    {payment.status}
                                  </span>
                                </td>
                                <td className="px-4 py-2 text-sm font-medium text-green-600 text-right">
                                  +{formatCurrency(payment.amount)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="p-6 text-center text-gray-500">
                  Invoice not found
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <div className="rounded-xl bg-white p-6 shadow-sm border border-gray-200">
          <span className="text-sm font-medium text-gray-500">Total Outstanding</span>
          <p className="mt-2 text-3xl font-bold text-gray-900">
            {formatCurrency(data.summary.totalOutstanding)}
          </p>
        </div>
        <div className="rounded-xl bg-white p-6 shadow-sm border border-gray-200">
          <span className="text-sm font-medium text-gray-500">Overdue</span>
          <p className="mt-2 text-3xl font-bold text-red-600">
            {formatCurrency(data.summary.totalOverdue)}
          </p>
        </div>
        <div className="rounded-xl bg-white p-6 shadow-sm border border-gray-200">
          <span className="text-sm font-medium text-gray-500">Paid This Period</span>
          <p className="mt-2 text-3xl font-bold text-green-600">
            {formatCurrency(data.summary.totalPaid)}
          </p>
        </div>
        <div className="rounded-xl bg-white p-6 shadow-sm border border-gray-200">
          <span className="text-sm font-medium text-gray-500">Total Invoices</span>
          <p className="mt-2 text-3xl font-bold text-gray-900">{data.summary.invoiceCount}</p>
        </div>
      </div>

      {/* Aging Report */}
      <div className="rounded-xl bg-white p-6 shadow-sm border border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Accounts Receivable Aging</h3>
        <div className="grid gap-4 sm:grid-cols-5">
          <div className="text-center p-4 bg-green-50 rounded-lg">
            <p className="text-sm text-green-600 font-medium">Current</p>
            <p className="text-xl font-bold text-green-700">{formatCurrency(data.aging.current)}</p>
          </div>
          <div className="text-center p-4 bg-yellow-50 rounded-lg">
            <p className="text-sm text-yellow-600 font-medium">1-30 Days</p>
            <p className="text-xl font-bold text-yellow-700">{formatCurrency(data.aging.thirtyDays)}</p>
          </div>
          <div className="text-center p-4 bg-orange-50 rounded-lg">
            <p className="text-sm text-orange-600 font-medium">31-60 Days</p>
            <p className="text-xl font-bold text-orange-700">{formatCurrency(data.aging.sixtyDays)}</p>
          </div>
          <div className="text-center p-4 bg-red-50 rounded-lg">
            <p className="text-sm text-red-600 font-medium">61-90 Days</p>
            <p className="text-xl font-bold text-red-700">{formatCurrency(data.aging.ninetyDays)}</p>
          </div>
          <div className="text-center p-4 bg-red-100 rounded-lg">
            <p className="text-sm text-red-700 font-medium">90+ Days</p>
            <p className="text-xl font-bold text-red-800">{formatCurrency(data.aging.overNinety)}</p>
          </div>
        </div>
      </div>

      {/* Invoices Table */}
      <div className="rounded-xl bg-white shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">Outstanding Invoices</h3>
          <button className="flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700">
            <Download className="h-4 w-4" />
            Export
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Invoice #
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Member
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Total
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Balance
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Due Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {data.invoices.map((invoice) => (
                <tr
                  key={invoice.id}
                  className="hover:bg-gray-50 cursor-pointer"
                  onClick={() => setSelectedInvoiceId(invoice.id)}
                >
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-primary-600 hover:text-primary-800 hover:underline">
                    {invoice.invoiceNumber}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {invoice.member.firstName} {invoice.member.lastName}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatCurrency(invoice.total)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {formatCurrency(invoice.balance)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(invoice.dueDate).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={cn('inline-flex rounded-full px-2 py-1 text-xs font-medium', getStatusColor(invoice.status))}>
                      {invoice.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function PaymentsTab({ onViewInvoice }: { onViewInvoice: () => void }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['payment-report'],
    queryFn: async () => {
      const response = await api.get<PaymentReport>('/reports/financial/payments');
      if (!response.success) throw new Error(response.error?.message);
      return response.data!;
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="rounded-lg bg-red-50 p-4 text-red-700">
        <div className="flex items-center gap-2">
          <AlertCircle className="h-5 w-5" />
          <span>Failed to load payment report</span>
        </div>
      </div>
    );
  }

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);

  const getMethodColor = (method: string) => {
    switch (method.toLowerCase()) {
      case 'card':
      case 'credit_card':
        return 'bg-purple-100 text-purple-700';
      case 'ach':
      case 'bank':
        return 'bg-blue-100 text-blue-700';
      case 'check':
        return 'bg-gray-100 text-gray-700';
      case 'cash':
        return 'bg-green-100 text-green-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid gap-4 sm:grid-cols-3">
        <div className="rounded-xl bg-white p-6 shadow-sm border border-gray-200">
          <span className="text-sm font-medium text-gray-500">Total Payments</span>
          <p className="mt-2 text-3xl font-bold text-gray-900">
            {formatCurrency(data.summary.totalAmount)}
          </p>
        </div>
        <div className="rounded-xl bg-white p-6 shadow-sm border border-gray-200">
          <span className="text-sm font-medium text-gray-500">Number of Payments</span>
          <p className="mt-2 text-3xl font-bold text-gray-900">{data.summary.totalPayments}</p>
        </div>
        <div className="rounded-xl bg-white p-6 shadow-sm border border-gray-200">
          <span className="text-sm font-medium text-gray-500">Average Payment</span>
          <p className="mt-2 text-3xl font-bold text-gray-900">
            {formatCurrency(data.summary.averageAmount)}
          </p>
        </div>
      </div>

      {/* By Method */}
      <div className="rounded-xl bg-white p-6 shadow-sm border border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">By Payment Method</h3>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {data.byMethod.map((method) => (
            <div key={method.method} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <span className={cn('inline-flex rounded-full px-2 py-1 text-xs font-medium', getMethodColor(method.method))}>
                  {method.method}
                </span>
                <p className="mt-2 text-xs text-gray-500">{method.count} payments</p>
              </div>
              <p className="text-lg font-bold text-gray-900">{formatCurrency(method.total)}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Payments Table */}
      <div className="rounded-xl bg-white shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">Recent Payments</h3>
          <button className="flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700">
            <Download className="h-4 w-4" />
            Export
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Member
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Invoice
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Method
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Date
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {data.payments.map((payment) => (
                <tr key={payment.id}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {payment.member
                      ? `${payment.member.firstName} ${payment.member.lastName}`
                      : 'Unknown'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    {payment.invoice?.invoiceNumber ? (
                      <button
                        onClick={onViewInvoice}
                        className="text-primary-600 hover:text-primary-800 hover:underline"
                      >
                        {payment.invoice.invoiceNumber}
                      </button>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-green-600">
                    +{formatCurrency(payment.amount)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={cn('inline-flex rounded-full px-2 py-1 text-xs font-medium', getMethodColor(payment.method))}>
                      {payment.method}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(payment.processedAt).toLocaleDateString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
